<template>
  <teleport to="body">
    <transition name="modal">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        @click.self="close"
      >
        <div
          class="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-dark-surface rounded-2xl shadow-2xl"
          @click.stop
        >
          <!-- Close Button -->
          <button
            @click="close"
            class="absolute top-4 right-4 p-2 rounded-lg bg-gray-100 dark:bg-dark-bg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200 z-10"
            aria-label="Close modal"
          >
            <X :size="24" class="text-gray-700 dark:text-gray-300" />
          </button>

          <!-- Modal Content -->
          <div v-if="project">
            <!-- Image -->
            <div class="relative h-80 overflow-hidden rounded-t-2xl">
              <img
                :src="project.image"
                :alt="project.title"
                class="w-full h-full object-cover"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

              <!-- Title Overlay -->
              <div class="absolute bottom-0 left-0 right-0 p-8 text-white">
                <span class="inline-block px-4 py-1.5 bg-accent rounded-full text-sm font-semibold mb-3">
                  {{ project.service }}
                </span>
                <h2 class="text-4xl font-display font-bold">{{ project.title }}</h2>
                <p class="text-lg mt-2 text-gray-200">{{ project.client }}</p>
              </div>
            </div>

            <!-- Details -->
            <div class="p-8">
              <!-- Description -->
              <div class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">
                  About the Project
                </h3>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {{ project.longDescription || project.description }}
                </p>
              </div>

              <!-- Challenge -->
              <div v-if="project.challenge" class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">The Challenge</h3>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">{{ project.challenge }}</p>
              </div>

              <!-- Approach -->
              <div v-if="project.approach" class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">Our Approach</h3>
                <p class="text-gray-600 dark:text-gray-300 leading-relaxed">{{ project.approach }}</p>
              </div>

              <!-- Outcomes -->
              <div v-if="project.outcomes?.length" class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">Key Outcomes</h3>
                <ul class="space-y-2">
                  <li v-for="(outcome, i) in project.outcomes" :key="i"
                      class="flex items-start gap-3 text-gray-600 dark:text-gray-300">
                    <span class="mt-1 w-2 h-2 rounded-full bg-accent shrink-0"></span>
                    {{ outcome }}
                  </li>
                </ul>
              </div>

              <!-- Metrics -->
              <div v-if="project.impact?.metrics?.length" class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">Impact</h3>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div v-for="(metric, i) in project.impact.metrics" :key="i"
                       class="p-4 bg-primary/5 dark:bg-accent/5 rounded-xl text-center">
                    <p class="text-2xl font-bold text-accent">{{ metric.value }}</p>
                    <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">{{ metric.label }}</p>
                  </div>
                </div>
              </div>

              <!-- Technologies -->
              <div class="mb-8">
                <h3 class="text-2xl font-display font-bold text-gray-900 dark:text-white mb-4">
                  Technologies Used
                </h3>
                <div class="flex flex-wrap gap-3">
                  <span
                    v-for="(tech, index) in project.technologies"
                    :key="index"
                    class="px-4 py-2 bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent font-semibold rounded-lg"
                  >
                    {{ tech }}
                  </span>
                </div>
              </div>

              <!-- CTA -->
              <div class="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-dark-border">
                <a
                  v-if="project.link && project.link !== '#'"
                  :href="project.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="btn-primary inline-flex items-center"
                >
                  Visit Project
                  <ExternalLink :size="20" class="ml-2" />
                </a>
                <button
                  @click="close"
                  class="btn-outline"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { watch } from 'vue';
import { X, ExternalLink } from 'lucide-vue-next';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  project: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['close']);

const close = () => {
  emit('close');
};

watch(() => props.isOpen, (newValue) => {
  if (newValue) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>
